/**
 * Maps institution display names (from API) to stable keys and static logo paths under /public.
 */
export const INSTITUTION_NAME_TO_KEY = {
    'The University of Hong Kong': 'hku',
    'The Chinese University of Hong Kong': 'cuhk',
    'The Hong Kong Polytechnic University': 'polyu',
    'City University of Hong Kong': 'cityu',
    'Lingnan University': 'lingnan',
    'Hong Kong Metropolitan University': 'hkmu',
    'The Hang Seng University of Hong Kong': 'hsu',
    'Saint Francis University': 'sfu',
    'Tung Wah College': 'twc',
    'UOW College Hong Kong': 'uowchk',
    'Hong Kong Baptist University': 'hkbu',
    'Hong Kong Chu Hai College': 'hkchu',
    'Hong Kong Shue Yan University': 'hksyu',
    'The Hong Kong University of Science and Technology': 'hkust',
    'The Education University of Hong Kong': 'eduhk',
    'Technological and Higher Education Institute of Hong Kong, Vocational Training Council': 'thei',
};

export function getInstitutionKey(institutionName) {
    return INSTITUTION_NAME_TO_KEY[institutionName] || institutionName;
}

export const INSTITUTION_SHORT_LABELS = {
    'The University of Hong Kong': { en: 'HKU', tc: '港大', sc: '港大' },
    'The Chinese University of Hong Kong': { en: 'CUHK', tc: '中大', sc: '中大' },
    'The Hong Kong University of Science and Technology': { en: 'HKUST', tc: '科大', sc: '科大' },
    'City University of Hong Kong': { en: 'CityU', tc: '城大', sc: '城大' },
    'The Hong Kong Polytechnic University': { en: 'PolyU', tc: '理大', sc: '理大' },
    'Hong Kong Baptist University': { en: 'HKBU', tc: '浸大', sc: '浸大' },
    'Lingnan University': { en: 'LingU', tc: '嶺大', sc: '岭大' },
    'Hong Kong Shue Yan University': { en: 'SYU', tc: '樹仁', sc: '树仁' },
    'The Education University of Hong Kong': { en: 'EdUHK', tc: '教大', sc: '教大' },
    'Hong Kong Metropolitan University': { en: 'HKMU', tc: '都會大學', sc: '都会大学' },
    'The Hang Seng University of Hong Kong': { en: 'HSUHK', tc: '恒大', sc: '恒大' },
    'Saint Francis University': { en: 'SFU', tc: '聖方濟各', sc: '圣方济各' },
    'Tung Wah College': { en: 'TWC', tc: '東華', sc: '东华' },
    'UOW College Hong Kong': { en: 'UOWCHK', tc: '伍倫貢', sc: '伍伦贡' },
    'Hong Kong Chu Hai College': { en: 'CHC', tc: '珠海學院', sc: '珠海学院' },
    'Technological and Higher Education Institute of Hong Kong, Vocational Training Council': { en: 'THEi', tc: '職訓局', sc: '职训局' },
};

export function getInstitutionShortLabel(institutionName, language = 'en') {
    const normalizedLanguage = language === 'tc' || language === 'sc' ? language : 'en';
    return INSTITUTION_SHORT_LABELS[institutionName]?.[normalizedLanguage] || institutionName;
}

const normalizeInstitutionAliasToken = (token) => String(token || '')
    .replace(/^[,.;:()[\]{}]+|[,.;:()[\]{}]+$/g, '')
    .toLocaleLowerCase('en');

/**
 * Extracts the first standalone institution alias from a free-text query.
 * Example: "hku bba" -> HKU institution + "bba".
 */
export function extractInstitutionAliasQuery(query) {
    const tokens = String(query || '').trim().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) {
        return { institution: null, query: '', alias: null };
    }

    const aliases = new Map();
    Object.entries(INSTITUTION_SHORT_LABELS).forEach(([institution, labels]) => {
        Object.values(labels || {}).filter(Boolean).forEach((alias) => {
            aliases.set(normalizeInstitutionAliasToken(alias), { institution, alias });
        });
    });

    const aliasIndex = tokens.findIndex((token) => (
        aliases.has(normalizeInstitutionAliasToken(token))
    ));
    if (aliasIndex < 0) {
        return { institution: null, query: tokens.join(' '), alias: null };
    }

    const match = aliases.get(normalizeInstitutionAliasToken(tokens[aliasIndex]));
    return {
        institution: match.institution,
        alias: match.alias,
        query: tokens.filter((_, index) => index !== aliasIndex).join(' '),
    };
}

/** File extension under public/asset/logos/institutions/ (default png — Wikimedia logo thumbs). */
const INSTITUTION_LOGO_EXT = {};

/**
 * When the on-disk filename or extension differs from {key}.{ext} (e.g. THEi uses VTC branding as vtc.png).
 */
const INSTITUTION_LOGO_OVERRIDE = {
    thei: { file: 'vtc' },
};

/**
 * Public URL for toggle logo (raster under public/asset/logos/institutions/).
 * Regenerate via: python3 client/scripts/download-institution-logos.py
 */
export function getInstitutionLogoSrc(institutionName) {
    if (!Object.prototype.hasOwnProperty.call(INSTITUTION_NAME_TO_KEY, institutionName)) return null;
    const key = INSTITUTION_NAME_TO_KEY[institutionName];
    const override = INSTITUTION_LOGO_OVERRIDE[key];
    const fileBase = override?.file ?? key;
    const ext = override?.ext ?? (INSTITUTION_LOGO_EXT[key] || 'png');
    const base = process.env.PUBLIC_URL === undefined ? '' : process.env.PUBLIC_URL;
    return `${base}/asset/logos/institutions/${fileBase}.${ext}`;
}
